import axios, { AxiosError } from 'axios';
import { authStorage } from '../utils/secureStorage';
import { logger } from '../utils/logger';
import type { UserData, User } from '../types/user';

// Seguro: acesso direto ao import.meta.env (substituído new Function por segurança)
export const API_URL = import.meta.env.VITE_API_URL || '/api';

const getEnvVar = (key: string): string | undefined => {
  // Acesso seguro às variáveis de ambiente
  return import.meta.env[key] as string | undefined;
};

// Interfaces para tipagem forte
interface ApiErrorResponse {
  detail?: string;
  error?: string;
  details?: string;
  message?: string;
}

interface AuthResponse {
  access: string;
  access_token?: string;
  refresh?: string;
  refresh_token?: string;
  user?: UserData;
  [key: string]: unknown;
}

// UserData is now imported from '../types/user'
// This provides a unified type system across the application

interface SocialLoginRequest {
  access_token: string;
  user_type?: string;
}

interface GoogleOAuthResponse {
  access_token: string;
  [key: string]: unknown;
}

interface FacebookAuthResponse {
  authResponse: {
    accessToken: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface GoogleOAuthError {
  error?: string;
  error_description?: string;
  [key: string]: unknown;
}

interface PasswordResetResponse {
  message?: string;
  detail?: string;
  [key: string]: unknown;
}

interface SubscriptionResponse {
  plan?: string;
  status?: string;
  [key: string]: unknown;
}

const login = async (email: string, password: string) => {
  try {
    logger.auth('Login service called', {
      email: email.substring(0, 3) + '***'
    });

    const response = await axios.post<AuthResponse>(`${API_URL}/auth/login/`, {
      email,
      password,
    });

    const data = response.data;

    if (data.access) {
      // Usar secure storage em vez de localStorage
      const tokenExpiry = 24; // 24 horas padrão
      await authStorage.setToken(data.access, tokenExpiry);

      if (data.refresh) {
        await authStorage.setRefreshToken(data.refresh, 168); // 7 dias
      }

      await authStorage.setUserData(data.user || (data as UserData), tokenExpiry);

      logger.auth('Login successful - data stored securely');
    }

    return data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    logger.error('Login service failed', {
      email: email.substring(0, 3) + '***',
      error: axiosError.message,
      status: axiosError.response?.status
    });
    throw axiosError.response?.data?.detail || 'Erro ao fazer login';
  }
};

const register = async (userData: {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  telefone?: string;
  tipo_usuario?: string;
  plano?: string;
  role?: string;
  terms_accepted?: boolean;
}, retries = 2) => {
  try {
    if (!import.meta.env.PROD) console.log('🔍 AUTH SERVICE - Payload enviado (email masked):', { email: userData.email?.substring(0,3)+'***' });
    logger.auth('Registration service called', {
      email: userData.email.substring(0, 3) + '***'
    });

    const response = await axios.post<AuthResponse>(`${API_URL}/auth/register/?t=${Date.now()}`, userData);

    const data = response.data;

    if (data.access) {
      // Usar secure storage em vez de localStorage
      const tokenExpiry = 24; // 24 horas padrão
      await authStorage.setToken(data.access, tokenExpiry);

      if (data.refresh) {
        await authStorage.setRefreshToken(data.refresh, 168); // 7 dias
      }

      await authStorage.setUserData(data.user || (data as UserData), tokenExpiry);

      logger.auth('Registration successful - data stored securely');
    }

    return data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;

    // Detectar erros de rede
    const isNetworkError =
      axiosError.message === 'Network Error' ||
      axiosError.code === 'ERR_NETWORK' ||
      axiosError.code === 'ERR_NETWORK_CHANGED' ||
      !axiosError.response;

    // Retry automático para erros de rede
    if (isNetworkError && retries > 0) {
      logger.warn(`Network error detected, retrying... (${retries} attempts left)`, {
        error: axiosError.message,
        code: axiosError.code
      });

      // Aguardar 1 segundo antes de tentar novamente
      await new Promise(resolve => setTimeout(resolve, 1000));

      return register(userData, retries - 1);
    }

    logger.error('Registration service failed', {
      email: userData.email.substring(0, 3) + '***',
      error: axiosError.message,
      status: axiosError.response?.status,
      code: axiosError.code
    });
    throw error;
  }
};

const logout = () => {
  logger.auth('Logout service called');
  authStorage.clearAuth();
  logger.security('All auth data cleared securely');
};

const requestPasswordReset = async (email: string): Promise<PasswordResetResponse> => {
  try {
    logger.auth('Password reset requested', { email: email.substring(0, 3) + '***' });
    const response = await axios.post<PasswordResetResponse>(`${API_URL}/auth/password-reset/`, { email });
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    logger.error('Password reset request failed', {
      email: email.substring(0, 3) + '***',
      status: axiosError.response?.status,
      details: axiosError.response?.data
    });
    throw axiosError.response?.data?.error || 'Erro ao solicitar redefinição de senha';
  }
};

const confirmPasswordReset = async (token: string, newPassword: string): Promise<PasswordResetResponse> => {
  try {
    logger.auth('Password reset confirmation requested', { token: token.slice(0, 6) + '***' });
    const response = await axios.post<PasswordResetResponse>(`${API_URL}/auth/password-reset/confirm/`, {
      token,
      new_password: newPassword
    });
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    logger.error('Password reset confirmation failed', {
      status: axiosError.response?.status,
      details: axiosError.response?.data
    });
    throw axiosError.response?.data?.error || 'Erro ao redefinir senha';
  }
};

// NOVO: Funções helper para o novo sistema de roles (agora assíncronas)
const getUserRole = async () => {
  try {
    const userData = await authStorage.getUserData();
    // Priorizar campo role sobre legado
    return userData?.role || userData?.tipo_usuario || null;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Failed to get user role', { error: errorMessage });
    return null;
  }
};

const isSystemAdmin = async () => {
  const role = await getUserRole();
  return role === 'SYSTEM_ADMIN';
};

const isBusinessOwner = async () => {
  const role = await getUserRole();
  return role === 'BUSINESS_OWNER';
};

const isProfessional = async () => {
  const role = await getUserRole();
  return role === 'PROFESSIONAL';
};

const isCustomer = async () => {
  const role = await getUserRole();
  return role === 'CUSTOMER';
};

// Legado: compatibilidade durante migração
const isAdmin = async () => {
  const role = await getUserRole();
  return role === 'SYSTEM_ADMIN' || role === 'BUSINESS_OWNER' || role === 'ADMIN';
};

const getCurrentUser = async () => {
  try {
    const userData = await authStorage.getUserData();
    if (userData) {
      logger.auth('Current user retrieved securely');
      return userData;
    }
    return null;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Failed to get current user', { error: errorMessage });
    return null;
  }
};

const getToken = async () => {
  try {
    const token = await authStorage.getToken();
    if (token) {
      logger.auth('Token retrieved securely');
    }
    return token;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Failed to get token', { error: errorMessage });
    return null;
  }
};

const getRefreshToken = async () => {
  try {
    const refreshToken = await authStorage.getRefreshToken();
    if (refreshToken) {
      logger.auth('Refresh token retrieved securely');
    }
    return refreshToken;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Failed to get refresh token', { error: errorMessage });
    return null;
  }
};

const isAuthenticated = async () => {
  try {
    if (!import.meta.env.PROD) console.log('AUTH_Service: Iniciando verificação isAuthenticated');
    const token = await authStorage.getToken();
    if (!import.meta.env.PROD) console.log('AUTH_Service: Token obtido:', !!token);
    if (!import.meta.env.PROD) console.log('AUTH_Service: API_URL:', API_URL);

    if (!token) {
      if (!import.meta.env.PROD) console.log('AUTH_Service: Token não encontrado, retornando false');
      return false;
    }

    if (!import.meta.env.PROD) console.log('AUTH_Service: Validando token com backend...');
    // Validar token com backend
    const response = await fetch(`${API_URL}/auth/profile/`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!import.meta.env.PROD) console.log('AUTH_Service: Resposta do backend:', response.status, response.statusText);

    if (response.ok) {
      if (!import.meta.env.PROD) console.log('AUTH_Service: Token validado com sucesso');
      logger.auth('Token validation successful');
      return true;
    } else {
      if (!import.meta.env.PROD) console.log('AUTH_Service: Token inválido, limpando storage');
      logger.auth('Token validation failed - clearing storage');
      authStorage.clearAuth();
      return false;
    }
  } catch (error) {
    console.error('AUTH_Service: Erro na verificação:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Failed to check authentication status', { error: errorMessage });
    return false;
  }
};

const getUserProfile = async (): Promise<UserData> => {
  const token = await getToken();

  if (!token) {
    logger.security('getUserProfile - Token não encontrado!');
    throw new Error('Não autenticado');
  }

  try {
    logger.api('Getting user profile');
    const response = await axios.get<UserData>(`${API_URL}/auth/profile/`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    logger.auth('User profile retrieved successfully');
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    logger.error('Failed to get user profile', {
      status: axiosError.response?.status,
      error: axiosError.response?.data
    });
    throw axiosError.response?.data?.detail || 'Erro ao buscar perfil';
  }
};

const getUserPlan = async (): Promise<SubscriptionResponse | null> => {
  try {
    const token = await getToken();
    if (!token) {
      throw new Error('No token available');
    }

    logger.api('Getting user plan information');
    const response = await axios.get<SubscriptionResponse>(`${API_URL}/billing/subscription/`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    logger.auth('User plan retrieved successfully');
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    logger.error('Failed to get user plan', {
      status: axiosError.response?.status,
      error: axiosError.response?.data
    });

    // Se não tiver assinatura, retorna null para indicar que não tem plano
    if (axiosError.response?.status === 404) {
      return null;
    }

    throw axiosError.response?.data?.detail || 'Erro ao buscar plano';
  }
};

// Social Login Functions
const socialLogin = async (provider: 'google' | 'facebook' | 'instagram', accessToken: string, userType?: string) => {
  try {
    logger.auth(`${provider} social login service called`, {
      provider,
      userType: userType || 'CLIENTE'
    });

    const endpoint = `${API_URL}/auth/social/${provider}/`;

    const requestData: SocialLoginRequest = {
      access_token: accessToken
    };

    if (userType) {
      requestData.user_type = userType;
    }

    const response = await axios.post<AuthResponse>(endpoint, requestData);

    const data = response.data;

    if (data.access || data.access_token) {
      // Usar secure storage para tokens sociais
      const tokenExpiry = 24; // 24 horas padrão
      const accessToken = data.access || data.access_token || '';
      await authStorage.setToken(accessToken, tokenExpiry);

      const refreshToken = data.refresh || data.refresh_token;
      if (refreshToken) {
        await authStorage.setRefreshToken(refreshToken, 168); // 7 dias
      }

      await authStorage.setUserData(data.user || (data as UserData), tokenExpiry);

      logger.auth(`${provider} social login successful - data stored securely`);
    }

    return data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    logger.error(`${provider} social login failed`, {
      provider,
      userType,
      error: axiosError.message,
      status: axiosError.response?.status,
      details: axiosError.response?.data
    });
    throw axiosError.response?.data?.error || axiosError.response?.data?.details || `Erro ao fazer login com ${provider}`;
  }
};

// Função para inicializar o Google OAuth
const initGoogleOAuth = () => {
  return new Promise<void>((resolve, reject) => {
    if (window.google?.accounts) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.google?.accounts) {
        resolve();
      } else {
        reject(new Error('Google SDK failed to load'));
      }
    };

    script.onerror = () => {
      reject(new Error('Failed to load Google SDK'));
    };

    document.head.appendChild(script);
  });
};

// Função para fazer login com Google usando o SDK
const googleLogin = async (userType?: string): Promise<AuthResponse> => {
  try {
    await initGoogleOAuth();

    return new Promise((resolve, reject) => {
      window.google.accounts.oauth2.initTokenClient({
        client_id: getEnvVar('VITE_GOOGLE_CLIENT_ID'),
        scope: 'profile email',
        callback: async (response) => {
          if (response.access_token) {
            try {
              const result = await socialLogin('google', response.access_token, userType);
              resolve(result);
            } catch (error) {
              reject(error);
            }
          } else {
            reject(new Error('Failed to get Google access token'));
          }
        },
        error_callback: (error) => {
          logger.error('Google OAuth error', { error: error instanceof Error ? error.message : String(error) });
          reject(new Error('Google login cancelled or failed'));
        }
      }).requestAccessToken();
    });
  } catch (error) {
    logger.error('Google login initialization failed', error);
    throw error;
  }
};

// Função para inicializar o Facebook SDK
const initFacebookSDK = () => {
  return new Promise<void>((resolve, reject) => {
    if (window.FB) {
      resolve();
      return;
    }

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: getEnvVar('VITE_FACEBOOK_APP_ID'),
        cookie: true,
        xfbml: true,
        version: 'v18.0'
      });
      resolve();
    };

    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;

    script.onerror = () => {
      reject(new Error('Failed to load Facebook SDK'));
    };

    document.head.appendChild(script);
  });
};

// Função para fazer login com Facebook
const facebookLogin = async (userType?: string): Promise<AuthResponse> => {
  try {
    await initFacebookSDK();

    return new Promise((resolve, reject) => {
      window.FB.login(async (response) => {
        if (response.authResponse) {
          try {
            const result = await socialLogin('facebook', response.authResponse.accessToken, userType);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('Facebook login cancelled or failed'));
        }
      }, {
        scope: 'email,public_profile'
      });
    });
  } catch (error) {
    logger.error('Facebook login initialization failed', error);
    throw error;
  }
};

// Função para fazer login com Instagram (redirecionamento OAuth)
const instagramLogin = (userType?: string) => {
  const clientId = getEnvVar('VITE_INSTAGRAM_CLIENT_ID');
  const redirectUri = `${window.location.origin}/auth/callback/instagram/`;

  // Guardar o tipo de usuário no sessionStorage para usar após o callback
  if (userType) {
    sessionStorage.setItem('instagram_user_type', userType);
  }

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: 'user_profile,user_media',
    response_type: 'code'
  });

  const instagramAuthUrl = `https://api.instagram.com/oauth/authorize?${params.toString()}`;
  window.location.href = instagramAuthUrl;
};

// Função para fazer login com Facebook via redirecionamento (fallback)
const facebookLoginRedirect = (userType?: string) => {
  // Guardar o tipo de usuário no sessionStorage para usar após o callback
  if (userType) {
    sessionStorage.setItem('facebook_user_type', userType);
  }

  const authUrl = getSocialLoginUrl('facebook');
  window.location.href = authUrl;
};

// Função para obter URL de login Facebook/Instagram (fallback)
const getSocialLoginUrl = (provider: 'facebook' | 'instagram') => {
  const baseUrl = `${API_URL}/auth/dj-rest-auth/${provider}/`;
  const redirectUri = `${window.location.origin}/auth/callback/${provider}/`;

  const params = new URLSearchParams({
    redirect_uri: redirectUri,
    response_type: 'code'
  });

  return `${baseUrl}?${params.toString()}`;
};

const getUserType = async () => {
  const user = await getCurrentUser();
  return user ? user.tipo_usuario : null;
};

const getDashboardRoute = async () => {
  const user = await getCurrentUser();
  if (!user) return '/login';

  // NOVO: Usar lógica do novo sistema de roles
  switch (user.role) {
    case 'SYSTEM_ADMIN':
      return '/dashboard';  // DashboardRouter vai decidir
    case 'BUSINESS_OWNER':
      return '/dashboard';
    case 'PROFISSIONAL':
      return '/dashboard';
    case 'CUSTOMER':
      return '/dashboard';
    default:
      // Legado: fallback para tipo_usuario
      switch (user.tipo_usuario) {
        case 'ADMIN':
        case 'proprietario':
          return '/dashboard';
        case 'PROFISSIONAL':
          return '/dashboard';
        default:
          return '/dashboard';
      }
  }
};

const authService = {
  login,
  register,
  logout,
  getCurrentUser,
  getToken,
  getRefreshToken,
  isAuthenticated,
  getUserProfile,
  getUserPlan,
  getUserType,
  getDashboardRoute,
  // NOVO: Funções helper para roles
  getUserRole,
  isSystemAdmin,
  isBusinessOwner,
  isProfessional,
  isCustomer,
  isAdmin,  // Legado
  // Social login functions
  socialLogin,
  googleLogin,
  facebookLogin,
  facebookLoginRedirect,
  instagramLogin,
  initGoogleOAuth,
  initFacebookSDK,
  getSocialLoginUrl,
  // Password reset
  requestPasswordReset,
  confirmPasswordReset,
};

export default authService;
